import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { CommonModule } from '@angular/common'
import { BehaviorSubject, combineLatest, map, Observable, withLatestFrom } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { TranslateModule } from '@ngx-translate/core'
import { IconLibrary } from '@galabid/gb-dashboard/shared/ui'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { GbPresenterCanvasComponent } from '../gb-presenter-canvas/gb-presenter-canvas.component'
import { ProjectorsFacade } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.facade'
import { Actions, ofType } from '@ngrx/effects'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'

@UntilDestroy()
@Component({
  selector: 'gb-presenter-view',
  imports: [CommonModule, TranslateModule, GbPresenterCanvasComponent, GbDashboardSharedUiModule],
  templateUrl: './gb-presenter-view.component.html',
  styleUrl: './gb-presenter-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterViewComponent implements OnInit, OnDestroy {
  @ViewChild('stageOuterContainer', { static: true }) stageOuterContainer!: ElementRef<HTMLElement>

  @ViewChild(GbPresenterCanvasComponent, { static: true }) canvasComponent!: GbPresenterCanvasComponent

  @Input() scale = 1
  @Input() previewMode = false

  IconLibrary = IconLibrary

  projectorContainerDataIsReady$ = this.projectorContainersFacade.projectorContainerDataIsReady$

  backgroundColor$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_color || '#ffffff'),
  )

  backgroundImageUrl$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image?.image || null),
  )

  backgroundImageCover$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image_cover ?? true),
  )

  customCss$ = this.projectorsFacade.selectedProjectors$.pipe(map((projector) => projector?.custom_css))

  private isFullscreenSubject = new BehaviorSubject<boolean>(false)
  isFullscreen$ = this.isFullscreenSubject.asObservable()

  private lastRealtimeDrawKey: string | null = null
  private lastRealtimeDrawAt = 0
  private readonly REALTIME_DRAW_DEDUPE_WINDOW_MS = 750

  private readonly fullscreenChangeHandler = () => {
    this.isFullscreenSubject.next(this.getIsFullscreen())
  }

  slides$ = this.projectorsFacade.allProjectors$.pipe(
    gbCheckLoaded(this.projectorContainerDataIsReady$),
    withLatestFrom(this.projectorContainersFacade.selectedProjectorContainer$),
    map(([projectors, selectedProjectorContainer]) => {
      return projectors.filter((projector) => projector.container === selectedProjectorContainer?.uuid)
    }),
  )

  currentSlideIndex$: Observable<number> = combineLatest([
    this.slides$,
    this.projectorsFacade.selectedProjectorId$,
  ]).pipe(map(([slides, selectedProjectorId]) => slides.map((slide) => slide.uuid).indexOf(selectedProjectorId || '')))

  currentSlideDisplayIndex$ = this.currentSlideIndex$.pipe(map((index) => (index >= 0 ? index + 1 : 1)))

  canGoPreviousSlide$ = combineLatest([this.currentSlideIndex$, this.slides$]).pipe(
    map(([index, slides]) => slides.length > 0 && index > 0),
  )

  canGoNextSlide$ = combineLatest([this.currentSlideIndex$, this.slides$]).pipe(
    map(([index, slides]) => slides.length > 0 && index >= 0 && index < slides.length - 1),
  )

  constructor(
    private projectorsFacade: ProjectorsFacade,
    private projectorContainersFacade: ProjectorContainersFacade,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    this.fullscreenChangeHandler()
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler)
    document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler as EventListener)

    this.actions$
      .pipe(ofType(PresenterWidgetsActions.triggerRaffleDrawFromRealtime), untilDestroyed(this))
      .subscribe((action) => {
        const key = `${action.widgetUuid}:${action.ticketNumber}:${action.prize}`
        const now = Date.now()

        if (this.lastRealtimeDrawKey === key && now - this.lastRealtimeDrawAt < this.REALTIME_DRAW_DEDUPE_WINDOW_MS) {
          return
        }

        this.lastRealtimeDrawKey = key
        this.lastRealtimeDrawAt = now

        this.canvasComponent?.triggerRaffleDrawFromRealtime(action.widgetUuid, action.ticketNumber, action.prize)
      })

    this.actions$
      .pipe(ofType(PresenterWidgetsActions.showProjectorNotification), untilDestroyed(this))
      .subscribe((action) => {
        this.canvasComponent?.triggerNotification(action.data)
      })
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler)
    document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.removeEventListener('MSFullscreenChange', this.fullscreenChangeHandler as EventListener)
  }

  toggleFullscreen(): void {
    if (this.getIsFullscreen()) {
      this.exitFullscreen()
      return
    }

    this.goFullscreen()
  }

  goFullscreen(): void {
    const fullscreenTarget = this.stageOuterContainer?.nativeElement
    if (!fullscreenTarget) return

    const doc = document as Document & {
      fullscreenElement?: Element | null
      webkitFullscreenElement?: Element | null
      mozFullScreenElement?: Element | null
      msFullscreenElement?: Element | null
    }

    const isFullscreen =
      !!doc.fullscreenElement ||
      !!doc.webkitFullscreenElement ||
      !!doc.mozFullScreenElement ||
      !!doc.msFullscreenElement

    if (isFullscreen) return

    const anyStage = fullscreenTarget as any
    if (anyStage.requestFullscreen) {
      void anyStage.requestFullscreen()
      return
    }

    if (anyStage.webkitRequestFullscreen) {
      void anyStage.webkitRequestFullscreen()
      return
    }

    if (anyStage.mozRequestFullScreen) {
      void anyStage.mozRequestFullScreen()
      return
    }

    if (anyStage.msRequestFullscreen) {
      void anyStage.msRequestFullscreen()
    }
  }

  exitFullscreen(): void {
    const doc = document as Document & {
      exitFullscreen?: () => Promise<void>
      webkitExitFullscreen?: () => Promise<void>
      mozCancelFullScreen?: () => Promise<void>
      msExitFullscreen?: () => Promise<void>
    }

    if (doc.exitFullscreen) {
      void doc.exitFullscreen()
      return
    }

    if (doc.webkitExitFullscreen) {
      void doc.webkitExitFullscreen()
      return
    }

    if (doc.mozCancelFullScreen) {
      void doc.mozCancelFullScreen()
      return
    }

    if (doc.msExitFullscreen) {
      void doc.msExitFullscreen()
    }
  }

  private getIsFullscreen(): boolean {
    const doc = document as Document & {
      fullscreenElement?: Element | null
      webkitFullscreenElement?: Element | null
      mozFullScreenElement?: Element | null
      msFullscreenElement?: Element | null
    }

    return (
      !!doc.fullscreenElement ||
      !!doc.webkitFullscreenElement ||
      !!doc.mozFullScreenElement ||
      !!doc.msFullscreenElement
    )
  }

  onGeneralClick() {
    if (!this.previewMode) this.selectNextSlide()
  }

  selectNextSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectNextSlide())
  }

  selectPreviousSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectPreviousSlide())
  }

  selectFirstSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectFirstSlide())
  }

  selectSlide(uuid: string) {
    this.projectorsFacade.dispatch(
      ProjectorActions.selectProjector({
        uuid,
      }),
    )
  }
}
